import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useEffect, useState } from 'react';

type LiveLinkDraft = {
  type?: string;
  name?: string;
  description?: string;
  path?: string;
  url?: string;
};

interface LiveLinkDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  initialValue?: LiveLinkDraft | null;
  onSubmit: (value: { type: 'url'; name: string; description: string; path: string }) => void;
  onDelete: () => void;
}

const defaultLiveLinkValue = {
  url: '',
  name: '',
  description: '',
};

function LiveLinkDialog({ open, setOpen, initialValue, onSubmit, onDelete }: Readonly<LiveLinkDialogProps>) {
  const [dialogValue, setDialogValue] = useState(defaultLiveLinkValue);

  useEffect(() => {
    if (!open) {
      setDialogValue(defaultLiveLinkValue);
      return;
    }

    setDialogValue({
      url: initialValue?.path ?? initialValue?.url ?? '',
      name: initialValue?.name ?? '',
      description: initialValue?.description ?? '',
    });
  }, [initialValue, open]);

  const handleClose = () => {
    setDialogValue(defaultLiveLinkValue);
    setOpen(false);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    onSubmit({
      type: 'url',
      name: dialogValue.name,
      description: dialogValue.description,
      path: dialogValue.url,
    });
    handleClose();
  };

  const isEditing = Boolean(initialValue);

  return (
    <Dialog open={open} onClose={handleClose}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{isEditing ? 'Update Live Link' : 'Add Live Link'}</DialogTitle>
        <DialogContent>
          <div>
            <TextField
              required
              autoFocus
              margin="dense"
              id="live-link-url"
              label="Live Link"
              type="text"
              variant="standard"
              value={dialogValue.url}
              onChange={(event) => setDialogValue((prev) => ({ ...prev, url: event.target.value }))}
            />
          </div>
          <div>
            <TextField
              required
              margin="dense"
              id="live-link-name"
              label="Link Name"
              type="text"
              variant="standard"
              value={dialogValue.name}
              onChange={(event) => setDialogValue((prev) => ({ ...prev, name: event.target.value }))}
            />
          </div>
          <div>
            <TextField
              margin="dense"
              id="live-link-description"
              label="Description"
              type="text"
              variant="standard"
              value={dialogValue.description}
              onChange={(event) => setDialogValue((prev) => ({ ...prev, description: event.target.value }))}
            />
          </div>
        </DialogContent>
        <DialogActions>
          {isEditing ? (
            <Button
              color="error"
              onClick={() => {
                onDelete();
                handleClose();
              }}
            >
              Delete
            </Button>
          ) : null}
          <Button onClick={handleClose}>Cancel</Button>
          <Button type="submit">{isEditing ? 'Save' : 'Add'}</Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default LiveLinkDialog;
